/**
 * i18n (web). Same exports as the mobile i18n.config.ts so vendored services
 * (`getCurrentLanguage`, `LANGUAGE_NAMES`, `getDateLocale`) resolve unchanged.
 * Resources come straight from the mobile locale files via the `@locales` alias.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  enUS, es as esLocale, fr as frLocale, de as deLocale, pt as ptLocale,
  pl as plLocale, zhTW, sv as svLocale, it as itLocale, hu as huLocale,
  ja as jaLocale, ko as koLocale, nl as nlLocale, tr as trLocale,
  type Locale,
} from 'date-fns/locale';

import en from '@locales/en.json';
import es from '@locales/es.json';
import fr from '@locales/fr.json';
import de from '@locales/de.json';
import pt from '@locales/pt.json';
import pl from '@locales/pl.json';
import zhHant from '@locales/zh-Hant.json';
import sv from '@locales/sv.json';
import it from '@locales/it.json';
import hu from '@locales/hu.json';
import ja from '@locales/ja.json';
import ko from '@locales/ko.json';
import nl from '@locales/nl.json';
import tr from '@locales/tr.json';

export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt', 'pl', 'zh-Hant', 'sv', 'it', 'hu', 'ja', 'ko', 'nl', 'tr'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  pl: 'Polski',
  'zh-Hant': '繁體中文',
  sv: 'Svenska',
  it: 'Italiano',
  hu: 'Magyar',
  ja: '日本語',
  ko: '한국어',
  nl: 'Nederlands',
  tr: 'Türkçe',
};

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  de: { translation: de },
  pt: { translation: pt },
  pl: { translation: pl },
  'zh-Hant': { translation: zhHant },
  sv: { translation: sv },
  it: { translation: it },
  hu: { translation: hu },
  ja: { translation: ja },
  ko: { translation: ko },
  nl: { translation: nl },
  tr: { translation: tr },
};

const LANGUAGE_STORAGE_KEY = 'app_language';

function applyLang(lang: string) {
  document.documentElement.lang = lang;
}

/** Map any detected tag (zh-TW, zh-Hant-HK, pt-BR, …) to a supported code. */
function normalize(tag: string): Language {
  if (tag.startsWith('zh')) return tag.includes('Hans') || tag.includes('CN') || tag.includes('SG') ? 'en' : 'zh-Hant';
  const base = tag.split('-')[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as Language) : 'en';
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    load: 'currentOnly',
    interpolation: { escapeValue: false },
    // A few keys are namespaces in the shared files; never render "[object Object]".
    returnedObjectHandler: (key: string) => key.split('.').pop() || key,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
      convertDetectedLanguage: (lng: string) => normalize(lng),
    },
  })
  .then(() => applyLang(i18n.resolvedLanguage || i18n.language));

i18n.on('languageChanged', applyLang);

export const loadSavedLanguage = async (): Promise<void> => {
  /* the browser detector already restored it */
};

export const changeLanguage = async (lang: Language): Promise<void> => {
  await i18n.changeLanguage(lang);
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
};

export const getCurrentLanguage = (): Language => normalize(i18n.resolvedLanguage || i18n.language || 'en');

const DATE_FNS_LOCALES: Record<Language, Locale> = {
  en: enUS, es: esLocale, fr: frLocale, de: deLocale, pt: ptLocale, pl: plLocale, 'zh-Hant': zhTW,
  sv: svLocale, it: itLocale, hu: huLocale, ja: jaLocale, ko: koLocale, nl: nlLocale, tr: trLocale,
};

/** date-fns locale for the active language (pass as `{ locale: getDateLocale() }`). */
export const getDateLocale = (): Locale => DATE_FNS_LOCALES[getCurrentLanguage()] || enUS;

export default i18n;
